import React, { useState, useEffect } from 'react';
import { FileText, Users, Globe, Shield, Scale } from 'lucide-react';
import { useTenant } from '../contexts/TenantContext';
import { MarketingLayout } from '../components/shell/MarketingLayout';
import { LegalHero } from '../components/shell/LegalHero';
import { SEO } from '../components/SEO';
import { cn } from '../lib/utils';

export const DpaPage = () => {
  const { brand, colorMode, activeBrandId } = useTenant();
  const theme = activeBrandId;
  const isDark = colorMode === 'dark';
  const brandName = brand?.name || 'Our Platform';

  const proseClasses = cn(
    "prose prose-lg max-w-none leading-relaxed",
    "prose-headings:font-black prose-headings:tracking-tight prose-p:text-black prose-li:text-black prose-strong:text-black dark:prose-headings:text-white dark:prose-p:text-white dark:prose-li:text-white dark:prose-strong:text-white"
  );

  const [activeSection, setActiveSection] = useState('intro');

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['intro', 'processing', 'obligations', 'subprocessors'];
      let current = sections[0];
      for (const section of sections) {
        const el = document.getElementById(section);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 150) {
            current = section;
          }
        }
      }
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 50) {
        current = sections[sections.length - 1];
      }
      setActiveSection(current);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { id: 'intro', label: '1. What is this?' },
    { id: 'processing', label: '2. Our Role' },
    { id: 'obligations', label: '3. Our Promises' },
    { id: 'subprocessors', label: '4. Subprocessors' },
  ];

  return (
    <MarketingLayout mainClassName="pt-28 pb-24 px-4 sm:px-6 lg:px-8">
      <SEO title="Data Processing Agreement" description={`Read the Data Processing Agreement (DPA) and data protection addendums for ${brandName}.`} />
        <div className="max-w-6xl mx-auto">
          
          <LegalHero 
            title="Data Processing Agreement"
            subtitle="Our legally-binding promise to you that we will handle your organization's data responsibly, securely, and respectfully."
            icon={<Shield size={14} />}
            label="Data Processing"
            version="Global Addendum"
            updated="Active"
          />

          <div className="flex flex-col md:flex-row gap-12 lg:gap-24 relative">
            
            {/* Sticky Navigation */}
            <aside className="md:w-64 flex-shrink-0 hidden md:block">
              <div className="sticky top-32 space-y-6">
                <h4 className={cn("font-bold uppercase text-[10px] tracking-widest font-mono", isDark ? "text-slate-500" : "text-slate-400")}>
                  On this page
                </h4>
                <nav className="flex flex-col gap-3 relative before:absolute before:inset-y-0 before:left-0 before:w-px before:bg-slate-200 dark:before:bg-slate-800">
                  {navItems.map((item) => (
                    <a 
                      key={item.id}
                      href={`#${item.id}`} 
                      className={cn(
                        "pl-4 py-1 text-sm font-medium transition-all duration-200 relative",
                        activeSection === item.id 
                          ? "text-brand-primary before:absolute before:inset-y-0 before:-left-px before:w-[2px] before:bg-brand-primary" 
                          : isDark ? "text-slate-400 hover:text-slate-200" : "text-slate-500 hover:text-slate-800"
                      )}
                    >
                      {item.label}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Content */}
            <div className="flex-1 max-w-3xl space-y-20">
              
              <section id="intro" className="scroll-mt-32">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-brand-primary/10 flex items-center justify-center text-brand-primary">
                    <FileText size={24} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold font-display">1. What is this?</h2>
                </div>
                <div className={proseClasses}>
                  <p>
                    This Data Processing Agreement (DPA) is a formal legal add-on to our Terms of Service. It's basically our legally-binding promise to you that we will handle your organization's data responsibly and respectfully.
                  </p>
                </div>
              </section>

              <section id="processing" className="scroll-mt-32">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-blue-500/10 flex items-center justify-center text-blue-500">
                    <Globe size={24} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold font-display">2. Our Role</h2>
                </div>
                <div className={proseClasses}>
                  <p>
                    We only process your data to keep the {brandName} platform running for you. We don't peek at it, and we don't sell it. Once you delete your account, we stop processing entirely and wipe it from our servers.
                  </p>
                </div>
              </section>

              <section id="obligations" className="scroll-mt-32">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-teal-500/10 flex items-center justify-center text-teal-500">
                    <Users size={24} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold font-display">3. Our Promises</h2>
                </div>
                <div className={proseClasses}>
                  <p>
                    We promise to only use your data exactly as you instruct us to through the app (like sending an email to a member when you click "Send"). Our team is bound by strict confidentiality agreements, so your organization's internal business stays private.
                  </p>
                </div>
              </section>

              <section id="subprocessors" className="scroll-mt-32">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-rose-500/10 flex items-center justify-center text-rose-500">
                    <Shield size={24} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold font-display">4. The Companies We Use</h2>
                </div>
                <div className={proseClasses}>
                  <p>
                    Sometimes we need help from other companies to run our service (like using AWS for cloud servers or SendGrid to send emails). When we share your data with them, we hold them to the exact same strict privacy standards that we hold ourselves to.
                  </p>
                </div>
              </section>

            </div>
          </div>
        </div>
    </MarketingLayout>
  );
};
